import { div, h1, p, span, button, fnstate } from '@srfnstack/fntags'

// A step-by-step view of every autonomous creation attempt — see exactly how far
// each product walked through the pipeline and where it stalled.
export const AutopilotPipeline = () => {
  const attempts = fnstate([])
  const loading = fnstate(true)
  const progress = fnstate(null)

  const load = async () => {
    loading(true)
    try {
      const r = await fetch('/api/system/autopilot/pipeline')
      attempts(r.ok ? await r.json() : [])
    } catch { attempts([]) }
    try {
      const pr = await fetch('/api/system/autopilot/progress')
      progress(pr.ok ? await pr.json() : null)
    } catch { progress(null) }
    loading(false)
  }

  const progressBanner = (p) => {
    if (!p) return span()
    const pct = p.target_per_day > 0 ? Math.min(100, Math.round((p.created_today / p.target_per_day) * 100)) : 0
    return div({ class: 'stats stats-horizontal shadow bg-base-200 border border-base-300 mb-4 w-full' },
      div({ class: 'stat' },
        div({ class: 'stat-title' }, 'Created (last 24h)'),
        div({ class: 'stat-value text-primary' }, `${p.created_today} / ${p.target_per_day}`),
        div({ class: 'stat-desc' }, p.autopilot_enabled
          ? (p.remaining_today > 0 ? `${p.remaining_today} more expected today` : 'daily target met')
          : 'autopilot is OFF')
      ),
      div({ class: 'stat' },
        div({ class: 'stat-title' }, 'This week'),
        div({ class: 'stat-value text-sm' }, `${p.created_this_week} / ${p.weekly_cap}`),
        div({ class: 'stat-desc' }, `${p.remaining_this_week} left before the weekly ceiling`)
      ),
      div({ class: 'stat hidden md:block' },
        div({ class: 'stat-title' }, 'Daily progress'),
        div({ class: 'stat-value text-sm' }, `${pct}%`),
        div({ class: 'stat-desc' }, progressElem(pct))
      )
    )
  }

  const progressElem = (pct) => div({ class: 'w-full bg-base-300 rounded-full h-2 mt-1' },
    div({ class: 'bg-primary h-2 rounded-full', style: `width: ${pct}%` })
  )

  const statusBadge = (s) => {
    const cls = s === 'PUBLISHED'
      ? 'badge-success'
      : s === 'FAILED'
        ? 'badge-error'
        : s === 'PENDING_ONE_CLICK_APPROVAL'
          ? 'badge-info'
          : s === 'AWAITING_COLOR_SELECTION'
            ? 'badge-warning'
            : s === 'DROPPED_LOW_QUALITY_ART'
              ? 'badge-error'
              : s === 'REJECTED_BY_USER'
                ? 'badge-error'
                : 'badge-ghost'
    return span({ class: `badge badge-sm ${cls}` }, s || 'in progress')
  }

  const deriveSteps = (row) => {
    const isTshirt = row.product_type === 't-shirt'
    const dropped = row.prompt_status === 'DROPPED_LOW_QUALITY_ART'
    const steps = [
      { label: 'Idea', done: true },
      { label: 'Artwork', done: row.artwork_ok > 0, fail: dropped },
      { label: 'Gate', done: !!row.product_id },
      { label: 'Setup', done: !!row.product_status && row.product_status !== 'PENDING' },
      ...(isTshirt ? [{ label: 'Colors', done: row.color_count > 0 }] : []),
      { label: 'Upscale', done: row.upscale_ok > 0 },
      { label: 'SEO', done: !!row.has_seo },
      { label: row.mockup_ok ? `Mockups (${row.mockup_ok})` : 'Mockups', done: row.mockup_ok > 0 },
      { label: 'Push', done: !!row.pushed },
      { label: 'Review', done: ['PENDING_ONE_CLICK_APPROVAL', 'PUBLISHED'].includes(row.product_status) },
      { label: 'Published', done: row.product_status === 'PUBLISHED' && row.linked }
    ]
    // When the attempt ended on a terminal failure/rejection, the frontier step
    // is where it died (red ✕) — not an in-progress step (blue ➤).
    const terminalFail = dropped || ['FAILED', 'REJECTED_BY_USER'].includes(row.product_status)
    let stopped = false
    for (const st of steps) {
      if (stopped) { st.state = 'todo'; continue }
      if (st.fail && !st.done) { st.state = 'fail'; stopped = true; continue }
      if (st.done) { st.state = 'done'; continue }
      st.state = terminalFail ? 'fail' : 'current'
      stopped = true
    }
    return steps
  }

  const stepPill = (st) => {
    const base = 'badge badge-sm gap-1 whitespace-nowrap '
    const cls = st.state === 'done'
      ? 'badge-success'
      : st.state === 'fail'
        ? 'badge-error'
        : st.state === 'current'
          ? 'badge-info animate-pulse'
          : 'badge-ghost opacity-50'
    const mark = st.state === 'done' ? '✓' : st.state === 'fail' ? '✕' : st.state === 'current' ? '➤' : '○'
    return span({ class: base + cls }, mark + ' ' + st.label)
  }

  const card = (row) => {
    const steps = deriveSteps(row)
    const stuck = steps.find(s => s.state === 'current' || s.state === 'fail')
    return div({ class: 'card bg-base-200 border border-base-300 p-3' },
      div({ class: 'flex flex-wrap items-center gap-2 mb-2' },
        span({ class: 'badge badge-ghost badge-sm' }, row.product_type || '?'),
        row.strategy ? span({ class: 'badge badge-outline badge-sm' }, row.strategy) : '',
        span({ class: 'font-semibold text-sm truncate flex-1 min-w-0' }, row.title || `attempt ${row.attempt_id}`),
        statusBadge(row.product_status || row.prompt_status)
      ),
      div({ class: 'flex items-center gap-1 overflow-x-auto pb-1' }, steps.map(stepPill)),
      (row.last_reason && (stuck && stuck.state !== 'done'))
        ? p({ class: 'text-xs opacity-70 mt-2 italic' }, `Latest note: "${row.last_reason}"`)
        : ''
    )
  }

  load()

  return div({ class: 'p-6' },
    div({ class: 'flex items-center gap-3 mb-1' },
      h1({ class: 'text-3xl font-bold' }, 'Autopilot Pipeline'),
      button({ class: 'btn btn-sm btn-ghost', onclick: load }, '↻ Refresh')
    ),
    p({ class: 'text-sm opacity-70 mb-4' }, 'Every autonomous creation attempt and how far it walked through the pipeline. A pulsing blue step is where it currently sits; red is where it dropped.'),
    progress.bindAs(progressBanner),
    loading.bindAs(l => l
      ? div({ class: 'flex justify-center p-12' }, span({ class: 'loading loading-spinner loading-lg' }))
      : attempts.bindAs(list => (!Array.isArray(list) || list.length === 0)
        ? div({ class: 'text-center opacity-60 p-12' }, 'No autonomous attempts yet — they appear once autopilot runs.')
        : div({ class: 'grid gap-3 max-w-4xl' }, list.map(card))))
  )
}
